<template>
  <v-dialog :model-value="modelValue" max-width="800px" @update:model-value="$emit('update:modelValue', $event)">
    <v-card style="border-radius: 18px !important;">
      <v-card-title class="dialog-header dialog-header-manage">
        <div class="d-flex align-center">
          <div class="header-icon-container">
            <v-avatar class="icon-background" size="46">
              <v-icon color="#4b2667" size="24">mdi-cog-outline</v-icon>
            </v-avatar>
          </div>
          <div>
            <div class="dialog-title">{{ $t('manageActionsTitle') }}</div>
            <div class="dialog-subtitle">{{ $t('manageActionsSubtitle') }}</div>
          </div>
        </div>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-card-title>

      <v-card-text class="pt-6">
        <v-row class="mb-2">
          <v-col cols="12" sm="7">
            <v-text-field
              v-model="search"
              :placeholder="$t('searchActions')"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              rounded
              clearable
              hide-details
            />
          </v-col>
          <v-col v-if="canEdit" cols="12" sm="5" class="d-flex justify-end align-center">
            <v-btn class="btn-layout" size="small" @click="addItem">
              <v-icon start size="18">mdi-plus-circle</v-icon>
              {{ $t('addAction') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          height="360"
        >
          <template v-slot:item.isActive="{ item }">
            <v-chip :color="item.isActive ? 'success' : 'error'" size="small" variant="outlined">
              {{ item.isActive ? $t('active') : $t('inactive') }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <div>
              <v-btn style="margin-right: 10px; height: 32px; width: 32px; border-radius: 6px;" size="small" variant="outlined" color="primary" :disabled="!canEdit" @click="editItem(item)">
                <v-icon size="16">mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">{{ $t('edit') }}</v-tooltip>
              </v-btn>
              <v-btn v-if="item.isActive" style="height: 32px; width: 32px; border-radius: 6px;" size="small" variant="outlined" color="error" :disabled="!canEdit" @click="deactivateItem(item)">
                <v-icon size="16">mdi-close-circle-outline</v-icon>
                <v-tooltip activator="parent" location="top">{{ $t('deactivate') }}</v-tooltip>
              </v-btn>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="48" color="grey">mdi-format-list-bulleted</v-icon>
              <p class="text-body-2 text-grey mt-2">{{ $t('noActionsYet') }}</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn style="border-radius: 12px; height: 44px; width: 100px;" variant="outlined" @click="close">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Add/Edit Action Dialog -->
    <v-dialog v-model="itemDialog" max-width="480px">
      <v-card style="border-radius: 18px !important;">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="dialog-title">{{ editMode ? $t('editActionTitle') : $t('addActionTitle') }}</div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="closeItemDialog" />
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.type"
                  :label="$t('actionType')"
                  variant="outlined"
                  :rules="[rules.required, rules.maxLength50]"
                  :maxlength="50"
                  counter
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.name"
                  :label="$t('actionName')"
                  variant="outlined"
                  :rules="[rules.required, rules.maxLength100]"
                  :maxlength="100"
                  counter
                  autofocus
                />
              </v-col>
              <v-col v-if="editMode" cols="12">
                <v-switch v-model="formData.isActive" :label="$t('active')" color="primary" inset hide-details />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn style="margin-right: 15px; border-radius: 12px; height: 44px; width: 90px;" variant="outlined" @click="closeItemDialog">{{ $t('cancel') }}</v-btn>
          <v-btn class="btn-layout" :loading="saving" @click="saveItem">{{ editMode ? $t('update') : $t('add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" location="top">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
export default {
  name: 'SuggestionActionManagerDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    isTaxPlanningMode: {
      type: Boolean,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      items: [],

      itemDialog: false,
      editMode: false,
      valid: false,

      formData: {
        id: null,
        type: 'func',
        name: '',
        isActive: true,
      },

      rules: {
        required: (value) => !!value || this.$t('fieldRequired'),
        maxLength50: (value) => !value || value.length <= 50 || this.$t('max50Characters'),
        maxLength100: (value) => !value || value.length <= 100 || this.$t('max100Characters'),
      },

      headers: [
        { title: this.$t('actionType'), key: 'type', sortable: true },
        { title: this.$t('actionName'), key: 'name', sortable: true },
        { title: this.$t('status'), key: 'isActive', sortable: true, align: 'center' },
        { title: this.$t('actions'), key: 'actions', sortable: false, align: 'center' },
      ],

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.items;
      const searchLower = this.search.toLowerCase();
      return this.items.filter(
        (item) => item.name?.toLowerCase().includes(searchLower) || item.type?.toLowerCase().includes(searchLower)
      );
    },
  },
  watch: {
    modelValue(isOpen) {
      if (isOpen) {
        this.loadActions();
      }
    },
  },
  methods: {
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    loadActions() {
      this.loading = true;
      this.$call.GetSuggestionActionsAdmin(
        this.isTaxPlanningMode,
        true,
        (dataResponse) => {
          this.items = (dataResponse && dataResponse.data) || [];
          this.loading = false;
        },
        (errorResource) => {
          this.loading = false;
          this.items = [];
          this.showSnackbar(this.$t('loadActionsFailed'), 'error');
          console.error(errorResource);
        }
      );
    },

    addItem() {
      this.editMode = false;
      this.formData = { id: null, type: 'func', name: '', isActive: true };
      this.itemDialog = true;
    },

    editItem(item) {
      this.editMode = true;
      this.formData = { id: item.id, type: item.type, name: item.name, isActive: item.isActive };
      this.itemDialog = true;
    },

    async saveItem() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.saving = true;

      const request = { create: [], update: [], delete: [] };

      if (this.editMode) {
        request.update = [{
          id: this.formData.id,
          type: this.formData.type?.trim() || '',
          name: this.formData.name?.trim() || '',
          isActive: this.formData.isActive,
        }];
      } else {
        request.create = [{
          type: this.formData.type?.trim() || '',
          name: this.formData.name?.trim() || '',
          isTaxPlanningMode: this.isTaxPlanningMode,
        }];
      }

      this.$call.BulkSaveSuggestionActionChanges(
        request,
        (dataResponse) => {
          this.saving = false;
          if (dataResponse.status === 0) {
            this.showSnackbar(this.editMode ? this.$t('updatedSuccessfully') : this.$t('addedSuccessfully'), 'success');
            this.loadActions();
            this.closeItemDialog();
            this.$emit('saved');
          } else {
            const errors = dataResponse?.data?.errors;
            this.showSnackbar((errors && errors[0]) || dataResponse.message || this.$t('saveFailed'), 'error');
          }
        },
        (errorResource) => {
          this.saving = false;
          this.showSnackbar(this.$t('saveFailed'), 'error');
          console.error(errorResource);
        }
      );
    },

    deactivateItem(item) {
      this.saving = true;
      const request = { create: [], update: [], delete: [item.id] };

      this.$call.BulkSaveSuggestionActionChanges(
        request,
        (dataResponse) => {
          this.saving = false;
          if (dataResponse.status === 0) {
            this.showSnackbar(this.$t('deactivatedSuccessfully'), 'success');
            this.loadActions();
            this.$emit('saved');
          } else {
            this.showSnackbar(dataResponse.message || this.$t('deactivateFailed'), 'error');
          }
        },
        (errorResource) => {
          this.saving = false;
          this.showSnackbar(this.$t('deactivateFailed'), 'error');
          console.error(errorResource);
        }
      );
    },

    closeItemDialog() {
      this.itemDialog = false;
      this.formData = { id: null, type: 'func', name: '', isActive: true };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    close() {
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.btn-layout {
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%) !important;
  border-radius: 16px;
  color: #fff !important;
  text-transform: none;
  font-weight: 500;
}

.btn-layout:hover {
  background: linear-gradient(90deg, #5a52a7 0%, #5b3077 100%) !important;
}

.dialog-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header-manage,
.dialog-header-add {
  background: linear-gradient(135deg, #f0e6f6 0%, #e8d5f0 100%);
}

.header-icon-container {
  margin-right: 16px;
}

.icon-background {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.dialog-subtitle {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

.my-data-table {
  border-radius: 8px;
}
</style>
